import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Container,
  Divider,
  Tooltip,
  IconButton,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  LocalHospital as TreatmentIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  MedicalServices as MedicalIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getTreatmentPlans } from '../../services/oncologyService';
import { getPatient } from '../../services/patientService';
import { PageHeader, HeaderButton, PageContainer, ContentCard } from './oncologyStyles';

const statusColors = {
  'Draft': 'default',
  'Active': 'success',
  'Completed': 'info',
  'On Hold': 'warning',
  'Cancelled': 'error'
};

const intentColors = {
  'Curative': 'success',
  'Neoadjuvant': 'primary',
  'Adjuvant': 'info',
  'Palliative': 'warning',
  'Maintenance': 'secondary'
};

const TreatmentPlanList = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { defaultFacility, currentUser } = useUserData();
  
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patient, setPatient] = useState(null);

  const loadData = useCallback(async () => {
    if (!defaultFacility?.id || !patientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Fetch patient info and treatment plans in parallel
      const [patientResult, plansResult] = await Promise.all([
        getPatient(patientId),
        getTreatmentPlans(patientId, defaultFacility.id, { limit: 100 })
      ]);
      
      setPatient(patientResult);
      setTreatmentPlans(plansResult.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load treatment plans');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id, patientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Helper to get patient initials
  const getPatientInitials = () => {
    if (!patient) return '?';
    const first = patient.firstName?.[0] || '';
    const last = patient.lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  // Helper to format patient name
  const getPatientFullName = () => {
    if (!patient) return 'Unknown Patient';
    return `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Unknown Patient';
  };

  // Helper to calculate age
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // If no patient ID, show message
  if (!patientId) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <PageHeader
            title="Treatment Plans"
            subtitle="View and manage cancer treatment plans"
            icon={<TreatmentIcon />}
            backTo="/oncology"
          />
          <ContentCard>
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select a patient to view treatment plans.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                To view treatment plans, navigate to a patient's profile and use the Oncology tab.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/patients')}
              >
                Go to Patients
              </Button>
            </CardContent>
          </ContentCard>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title="Treatment Plans"
          subtitle="Oncology treatment planning and management"
          icon={<TreatmentIcon />}
          backTo={`/patients/${patientId}`}
          actions={
            <HeaderButton
              startIcon={<AddIcon />}
              onClick={() => navigate(`/oncology/treatment-plan/new/${patientId}`)}
            >
              New Treatment Plan
            </HeaderButton>
          }
        />

        {/* Patient Info Card */}
        {patient && (
          <Card 
            sx={{ 
              mb: 3, 
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: '1px solid #e2e8f0'
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: '#7c3aed',
                      fontSize: '1.25rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {getPatientInitials()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#1e293b' }}>
                      {getPatientFullName()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                      {patient.mrn && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>MRN:</strong> {patient.mrn}
                        </Typography>
                      )}
                      {patient.dateOfBirth && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>DOB:</strong> {formatDate(patient.dateOfBirth)}
                        </Typography>
                      )}
                      {patient.dateOfBirth && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Age:</strong> {calculateAge(patient.dateOfBirth)} years
                        </Typography>
                      )}
                      {patient.gender && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Gender:</strong> {patient.gender}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => navigate(`/patients/${patientId}`)}
                  sx={{ 
                    borderColor: '#7c3aed', 
                    color: '#7c3aed',
                    '&:hover': { borderColor: '#6d28d9', bgcolor: 'rgba(124, 58, 237, 0.04)' }
                  }}
                >
                  View Patient Profile
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        <ContentCard>
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : treatmentPlans.length === 0 ? (
              <Box textAlign="center" py={4}>
                <TreatmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Treatment Plans Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  This patient doesn't have any treatment plans yet.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/oncology/treatment-plan/new/${patientId}`)}
                  sx={{ 
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)' }
                  }}
                >
                  Create First Treatment Plan
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {treatmentPlans.length} treatment plan{treatmentPlans.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {treatmentPlans.map((plan) => (
                    <Grid item xs={12} key={plan.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          borderRadius: '12px',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            borderColor: '#7c3aed'
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" fontWeight="bold" gutterBottom>
                                {plan.planName || 'Untitled Plan'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Chip 
                                  label={plan.status || 'Draft'} 
                                  color={statusColors[plan.status] || 'default'}
                                  size="small"
                                />
                                {plan.intent && (
                                  <Chip 
                                    label={plan.intent} 
                                    color={intentColors[plan.intent] || 'default'}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </Box>
                            <Box>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/oncology/treatment-plan/view/${plan.id}`)}
                                  sx={{ color: '#7c3aed' }}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Plan">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/oncology/treatment-plan/edit/${plan.id}`)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MedicalIcon fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Modalities
                                  </Typography>
                                  <Typography variant="body2">
                                    {plan.modalities?.join(', ') || 'Not specified'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScheduleIcon fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Start Date
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatDate(plan.plannedStartDate)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScheduleIcon fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    End Date
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatDate(plan.estimatedEndDate)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Created
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatDate(plan.createdAt)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Treatment Goals */}
                          {plan.treatmentGoals?.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Treatment Goals:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {plan.treatmentGoals.map((goal, idx) => (
                                  <Chip 
                                    key={idx}
                                    label={goal} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Chemotherapy Info */}
                          {plan.chemotherapy && (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(124, 58, 237, 0.05)', borderRadius: '8px' }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                Chemotherapy:
                              </Typography>
                              <Typography variant="body2">
                                {plan.chemotherapy.regimenName} • {plan.chemotherapy.plannedCycles || '?'} cycles planned
                                {plan.chemotherapy.startDate && ` • Starting ${formatDate(plan.chemotherapy.startDate)}`}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </CardContent>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

export default TreatmentPlanList;
